import { notify } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  CameraIcon,
  DeleteIcon,
  EditIcon,
  FlipDirectionIcon,
  ImageIcon,
  PasteIcon,
  UploadIcon,
} from '@blocksuite/icons/rc';
import * as lz from 'lz-string';
import {
  type ChangeEvent,
  type ClipboardEvent as ReactClipboardEvent,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as styles from './styles.css';

const PDF_SIGNATURE_INPUT_ACCEPT = 'image/*';
const PDF_SIGNATURE_DEFAULT_NAME = 'Signature';
const PDF_SIGNATURE_MAX_RENDER_WIDTH = 1400;
const PDF_SIGNATURE_PREVIEW_MIN_ZOOM = 1;
const PDF_SIGNATURE_PREVIEW_MAX_ZOOM = 4;
const PDF_SIGNATURE_PREVIEW_RENDER_WIDTH = 1200;
const PDF_SIGNATURE_PREVIEW_RENDER_HEIGHT = 400;

export type PdfSignatureCreatorMode = 'upload' | 'draw' | 'paste' | 'camera';

export type PdfSignatureCreatorResult = {
  file: File;
  suggestedName?: string;
};

export type PdfSignatureCreatorBlobResolver = (
  blobId: string
) => Promise<Blob | File | null | undefined>;

type PdfSignatureSourceFile = {
  file: File;
  suggestedName?: string;
};

type PdfSignaturePreviewTransform = {
  zoom: number;
  translateX: number;
  translateY: number;
};

type PdfSignaturePreviewInitialFit = 'default' | 'horizontal';

type PdfSignaturePreviewViewportTransform = PdfSignaturePreviewTransform & {
  viewportWidth: number;
  viewportHeight: number;
};

type PdfSignaturePreviewPointer = {
  x: number;
  y: number;
};

type PdfSignaturePreviewGesture = {
  startTransform: PdfSignaturePreviewTransform;
  startPointers: PdfSignaturePreviewPointer[];
};

type PdfSignaturePreviewNativeGesture = {
  startAnchor: PdfSignaturePreviewPointer;
  startTransform: PdfSignaturePreviewTransform;
};

class PdfSignaturePreparationError extends Error {
  constructor(
    readonly code:
      | 'clipboard-empty'
      | 'clipboard-unavailable'
      | 'camera-unavailable'
      | 'draw-empty'
      | 'image-conversion'
      | 'unsupported'
  ) {
    super(code);
  }
}

function getPdfSignaturePreparationErrorCode(error: unknown) {
  return error instanceof PdfSignaturePreparationError
    ? error.code
    : 'unsupported';
}

function getDefaultSignaturePreviewTransform(): PdfSignaturePreviewTransform {
  return {
    zoom: 1,
    translateX: 0,
    translateY: 0,
  };
}

function clampSignaturePreviewZoom(value: number) {
  if (!Number.isFinite(value)) return PDF_SIGNATURE_PREVIEW_MIN_ZOOM;
  return Math.min(
    PDF_SIGNATURE_PREVIEW_MAX_ZOOM,
    Math.max(PDF_SIGNATURE_PREVIEW_MIN_ZOOM, value)
  );
}

function clampSignaturePreviewTransform(
  transform: PdfSignaturePreviewTransform,
  viewport?: { width: number; height: number } | null
): PdfSignaturePreviewTransform {
  const zoom = clampSignaturePreviewZoom(transform.zoom);
  if (zoom <= PDF_SIGNATURE_PREVIEW_MIN_ZOOM + 0.001) {
    return getDefaultSignaturePreviewTransform();
  }
  const maxX = (viewport?.width ?? 320) * Math.max(0, zoom - 1) * 0.5;
  const maxY = (viewport?.height ?? 120) * Math.max(0, zoom - 1) * 0.5;
  return {
    zoom,
    translateX: Math.min(maxX, Math.max(-maxX, transform.translateX)),
    translateY: Math.min(maxY, Math.max(-maxY, transform.translateY)),
  };
}

function zoomSignaturePreviewTransformAtAnchor(
  transform: PdfSignaturePreviewTransform,
  nextZoom: number,
  anchor: PdfSignaturePreviewPointer,
  viewport: { width: number; height: number }
): PdfSignaturePreviewTransform {
  const zoom = clampSignaturePreviewZoom(nextZoom);
  const currentZoom = Math.max(PDF_SIGNATURE_PREVIEW_MIN_ZOOM, transform.zoom);
  const ratio = zoom / currentZoom;
  const anchorX = anchor.x - viewport.width / 2;
  const anchorY = anchor.y - viewport.height / 2;
  return clampSignaturePreviewTransform(
    {
      zoom,
      translateX: anchorX - (anchorX - transform.translateX) * ratio,
      translateY: anchorY - (anchorY - transform.translateY) * ratio,
    },
    viewport
  );
}

function zoomSignaturePreviewTransformBetweenAnchors(
  transform: PdfSignaturePreviewTransform,
  nextZoom: number,
  startAnchor: PdfSignaturePreviewPointer,
  currentAnchor: PdfSignaturePreviewPointer,
  viewport: { width: number; height: number }
): PdfSignaturePreviewTransform {
  const zoom = clampSignaturePreviewZoom(nextZoom);
  const currentZoom = Math.max(PDF_SIGNATURE_PREVIEW_MIN_ZOOM, transform.zoom);
  const ratio = zoom / currentZoom;
  const startX = startAnchor.x - viewport.width / 2;
  const startY = startAnchor.y - viewport.height / 2;
  const currentX = currentAnchor.x - viewport.width / 2;
  const currentY = currentAnchor.y - viewport.height / 2;
  return clampSignaturePreviewTransform(
    {
      zoom,
      translateX: currentX - (startX - transform.translateX) * ratio,
      translateY: currentY - (startY - transform.translateY) * ratio,
    },
    viewport
  );
}

function getHorizontalFitSignaturePreviewTransform(
  image: HTMLImageElement,
  viewport: { width: number; height: number }
): PdfSignaturePreviewTransform {
  if (
    image.naturalWidth <= 0 ||
    image.naturalHeight <= 0 ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    return getDefaultSignaturePreviewTransform();
  }

  const baseScale = Math.min(
    viewport.width / image.naturalWidth,
    viewport.height / image.naturalHeight
  );
  const baseWidth = image.naturalWidth * baseScale;
  if (baseWidth <= 0) return getDefaultSignaturePreviewTransform();

  return clampSignaturePreviewTransform(
    {
      zoom: viewport.width / baseWidth,
      translateX: 0,
      translateY: 0,
    },
    viewport
  );
}

function getPreviewPointerCenter(points: PdfSignaturePreviewPointer[]) {
  const total = points.reduce(
    (sum, point) => ({
      x: sum.x + point.x,
      y: sum.y + point.y,
    }),
    { x: 0, y: 0 }
  );
  return {
    x: total.x / points.length,
    y: total.y / points.length,
  };
}

function getPreviewPointerDistance(points: PdfSignaturePreviewPointer[]) {
  if (points.length < 2) return 1;
  const [first, second] = points;
  return Math.max(1, Math.hypot(second.x - first.x, second.y - first.y));
}

export function getPdfSignatureFileNameWithoutExtension(
  name: string | undefined
) {
  const trimmed = name?.trim();
  if (!trimmed) return '';
  return trimmed.replace(/\.[^.]+$/, '');
}

function getPlatformPasteShortcut() {
  if (typeof navigator === 'undefined') return 'Ctrl+V';
  const platform = navigator.platform || '';
  const userAgent = navigator.userAgent || '';
  return /Mac|iPhone|iPad|iPod/.test(`${platform} ${userAgent}`)
    ? '⌘V'
    : 'Ctrl+V';
}

function getSignatureInputKind(file: Pick<File, 'name' | 'type'>) {
  const type = file.type.toLowerCase().split(';')[0];
  const name = file.name.toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (/\.(avif|bmp|gif|heic|heif|jpeg|jpg|png|svg|tif|tiff|webp)$/.test(name)) {
    return 'image';
  }
  return null;
}

function getSignatureFileFromList(files: FileList | readonly File[]) {
  for (const file of Array.from(files)) {
    if (getSignatureInputKind(file)) return file;
  }
  return null;
}

function getClipboardBlobFileName(id: string, type: string | undefined) {
  if (id.includes('.')) return id;
  if (type === 'image/jpeg' || type === 'image/jpg') return `${id}.jpg`;
  if (type === 'image/webp') return `${id}.webp`;
  return `${id}.png`;
}

function decodeBase64File(content: string, name: string, type: string) {
  const binary = atob(content);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new File([bytes], name, { type });
}

function parseBlocksuiteClipboardHtml(html: string) {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const snapshotNode = doc.querySelector<HTMLElement>(
    '[data-blocksuite-snapshot]'
  );
  const compressed = snapshotNode?.dataset.blocksuiteSnapshot;
  if (!compressed) return null;
  const decompressed = lz.decompressFromEncodedURIComponent(compressed);
  if (!decompressed) return null;
  return JSON.parse(decompressed) as unknown;
}

function tryParseJson(value: string) {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function extractSignatureFileFromClipboardPayload(
  payload: unknown
): PdfSignatureSourceFile | null {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  const blobs = record.blobs;
  if (blobs && typeof blobs === 'object') {
    for (const [id, value] of Object.entries(
      blobs as Record<string, Record<string, unknown>>
    )) {
      const type = typeof value.type === 'string' ? value.type : '';
      const name =
        typeof value.name === 'string'
          ? value.name
          : getClipboardBlobFileName(id, type);
      const content = typeof value.content === 'string' ? value.content : '';
      if (!content) continue;
      const file = decodeBase64File(content, name, type);
      if (!getSignatureInputKind(file)) continue;
      return {
        file,
        suggestedName: getPdfSignatureFileNameWithoutExtension(name),
      };
    }
  }
  for (const value of Object.values(record)) {
    const nested =
      typeof value === 'string' ? (tryParseJson(value) ?? value) : value;
    if (typeof nested === 'string') continue;
    const file = extractSignatureFileFromClipboardPayload(nested);
    if (file) return file;
  }
  return null;
}

function collectSignatureBlobIdsFromClipboardPayload(
  payload: unknown,
  ids = new Set<string>()
) {
  if (!payload || typeof payload !== 'object') return ids;
  if (Array.isArray(payload)) {
    payload.forEach(item =>
      collectSignatureBlobIdsFromClipboardPayload(item, ids)
    );
    return ids;
  }
  for (const [key, value] of Object.entries(
    payload as Record<string, unknown>
  )) {
    if (
      (key === 'sourceId' || key === 'blobId') &&
      typeof value === 'string' &&
      value
    ) {
      ids.add(value);
      continue;
    }
    const nested =
      typeof value === 'string' ? (tryParseJson(value) ?? value) : value;
    if (typeof nested !== 'string') {
      collectSignatureBlobIdsFromClipboardPayload(nested, ids);
    }
  }
  return ids;
}

function getCanvas2D(canvas: HTMLCanvasElement, willReadFrequently = false) {
  const context = canvas.getContext('2d', { willReadFrequently });
  if (!context) {
    throw new PdfSignaturePreparationError('image-conversion');
  }
  return context;
}

function cloneCanvas(source: HTMLCanvasElement) {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  getCanvas2D(canvas).drawImage(source, 0, 0);
  return canvas;
}

function removeNearWhitePixels(canvas: HTMLCanvasElement) {
  const context = getCanvas2D(canvas, true);
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  let nonWhitePixels = 0;
  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3];
    if (alpha === 0) continue;
    const red = data[index];
    const green = data[index + 1];
    const blue = data[index + 2];
    if (red > 244 && green > 244 && blue > 244) {
      data[index + 3] = 0;
    } else {
      nonWhitePixels++;
    }
  }
  if (nonWhitePixels === 0) return false;
  context.putImageData(imageData, 0, 0);
  return true;
}

function cropCanvasVisibleContent(canvas: HTMLCanvasElement, padding = 10) {
  const context = getCanvas2D(canvas, true);
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const alpha = data[(y * canvas.width + x) * 4 + 3];
      if (alpha < 12) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < minX || maxY < minY) return null;
  const left = Math.max(0, minX - padding);
  const top = Math.max(0, minY - padding);
  const right = Math.min(canvas.width, maxX + padding);
  const bottom = Math.min(canvas.height, maxY + padding);
  const width = Math.max(1, right - left + 1);
  const height = Math.max(1, bottom - top + 1);
  const cropped = document.createElement('canvas');
  cropped.width = width;
  cropped.height = height;
  getCanvas2D(cropped).drawImage(
    canvas,
    left,
    top,
    width,
    height,
    0,
    0,
    width,
    height
  );
  return cropped;
}

function scaleCanvasToSignatureLimit(canvas: HTMLCanvasElement) {
  const maxSide = Math.max(canvas.width, canvas.height);
  if (maxSide <= PDF_SIGNATURE_MAX_RENDER_WIDTH) return canvas;
  const scale = PDF_SIGNATURE_MAX_RENDER_WIDTH / maxSide;
  const scaled = document.createElement('canvas');
  scaled.width = Math.max(1, Math.round(canvas.width * scale));
  scaled.height = Math.max(1, Math.round(canvas.height * scale));
  getCanvas2D(scaled).drawImage(canvas, 0, 0, scaled.width, scaled.height);
  return scaled;
}

function prepareSignatureCanvas(
  source: HTMLCanvasElement,
  options: { removeWhiteBackground: boolean }
) {
  const canvas = cloneCanvas(source);
  if (options.removeWhiteBackground) {
    removeNearWhitePixels(canvas);
  }
  const cropped = cropCanvasVisibleContent(canvas);
  if (!cropped) {
    throw new PdfSignaturePreparationError('draw-empty');
  }
  return scaleCanvasToSignatureLimit(cropped);
}

function captureVideoObjectFitCoverRegionToCanvas(
  video: HTMLVideoElement,
  target: HTMLElement | null
) {
  const videoWidth = video.videoWidth;
  const videoHeight = video.videoHeight;
  const videoRect = video.getBoundingClientRect();
  const targetRect = target?.getBoundingClientRect();
  if (
    !targetRect ||
    videoWidth <= 0 ||
    videoHeight <= 0 ||
    videoRect.width <= 0 ||
    videoRect.height <= 0
  ) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, videoWidth);
    canvas.height = Math.max(1, videoHeight);
    getCanvas2D(canvas).drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  const scale = Math.max(
    videoRect.width / videoWidth,
    videoRect.height / videoHeight
  );
  const renderedWidth = videoWidth * scale;
  const renderedHeight = videoHeight * scale;
  const offsetX = (videoRect.width - renderedWidth) / 2;
  const offsetY = (videoRect.height - renderedHeight) / 2;
  const sourceLeft = (targetRect.left - videoRect.left - offsetX) / scale;
  const sourceTop = (targetRect.top - videoRect.top - offsetY) / scale;
  const sourceRight = (targetRect.right - videoRect.left - offsetX) / scale;
  const sourceBottom = (targetRect.bottom - videoRect.top - offsetY) / scale;
  const left = Math.max(0, Math.min(videoWidth - 1, sourceLeft));
  const top = Math.max(0, Math.min(videoHeight - 1, sourceTop));
  const right = Math.max(left + 1, Math.min(videoWidth, sourceRight));
  const bottom = Math.max(top + 1, Math.min(videoHeight, sourceBottom));
  const width = Math.max(1, Math.round(right - left));
  const height = Math.max(1, Math.round(bottom - top));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  getCanvas2D(canvas).drawImage(
    video,
    left,
    top,
    right - left,
    bottom - top,
    0,
    0,
    width,
    height
  );
  return canvas;
}

function canvasToPngFile(canvas: HTMLCanvasElement, name: string) {
  return new Promise<File>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new PdfSignaturePreparationError('image-conversion'));
        return;
      }
      resolve(
        new File(
          [blob],
          `${getPdfSignatureFileNameWithoutExtension(name) || PDF_SIGNATURE_DEFAULT_NAME}.png`,
          {
            type: 'image/png',
          }
        )
      );
    }, 'image/png');
  });
}

async function loadImageFileToCanvas(file: File) {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    getCanvas2D(canvas).drawImage(image, 0, 0);
    return canvas;
  } catch {
    throw new PdfSignaturePreparationError('image-conversion');
  } finally {
    URL.revokeObjectURL(url);
  }
}

function renderSignaturePreviewTransformToCanvas(
  source: HTMLCanvasElement,
  transform: PdfSignaturePreviewViewportTransform
) {
  const canvas = document.createElement('canvas');
  canvas.width = PDF_SIGNATURE_PREVIEW_RENDER_WIDTH;
  canvas.height = PDF_SIGNATURE_PREVIEW_RENDER_HEIGHT;
  const context = getCanvas2D(canvas);
  const viewportWidth = Math.max(1, transform.viewportWidth);
  const viewportHeight = Math.max(1, transform.viewportHeight);
  const translateX =
    transform.translateX * (PDF_SIGNATURE_PREVIEW_RENDER_WIDTH / viewportWidth);
  const translateY =
    transform.translateY *
    (PDF_SIGNATURE_PREVIEW_RENDER_HEIGHT / viewportHeight);
  const baseScale = Math.min(
    PDF_SIGNATURE_PREVIEW_RENDER_WIDTH / source.width,
    PDF_SIGNATURE_PREVIEW_RENDER_HEIGHT / source.height
  );

  context.save();
  context.translate(
    PDF_SIGNATURE_PREVIEW_RENDER_WIDTH / 2 + translateX,
    PDF_SIGNATURE_PREVIEW_RENDER_HEIGHT / 2 + translateY
  );
  context.scale(baseScale * transform.zoom, baseScale * transform.zoom);
  context.drawImage(source, -source.width / 2, -source.height / 2);
  context.restore();

  return canvas;
}

async function normalizeSignatureAppearanceFile(
  file: File,
  transform?: PdfSignaturePreviewViewportTransform
) {
  if (!getSignatureInputKind(file)) {
    throw new PdfSignaturePreparationError('unsupported');
  }
  const sourceCanvas = await loadImageFileToCanvas(file);
  const transformedCanvas = transform
    ? renderSignaturePreviewTransformToCanvas(sourceCanvas, transform)
    : sourceCanvas;
  const signatureCanvas = prepareSignatureCanvas(transformedCanvas, {
    removeWhiteBackground: true,
  });
  return canvasToPngFile(signatureCanvas, file.name);
}

function getSignatureCanvasPoint(
  event: ReactPointerEvent<HTMLCanvasElement>,
  canvas: HTMLCanvasElement
) {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.width,
    y: ((event.clientY - rect.top) / rect.height) * canvas.height,
  };
}

function resetSignatureDrawCanvas(canvas: HTMLCanvasElement) {
  const context = getCanvas2D(canvas);
  context.save();
  context.globalCompositeOperation = 'source-over';
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.restore();
}

export interface PdfSignatureCreatorProps {
  isSaving?: boolean;
  onCreate: (result: PdfSignatureCreatorResult) => Promise<boolean> | boolean;
  resolveClipboardBlob?: PdfSignatureCreatorBlobResolver;
}

export function PdfSignatureCreator({
  isSaving = false,
  onCreate,
  resolveClipboardBlob,
}: PdfSignatureCreatorProps) {
  const t = useI18n();
  const pasteShortcut = useMemo(getPlatformPasteShortcut, []);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(
    null
  );
  const [creatorMode, setCreatorMode] =
    useState<PdfSignatureCreatorMode>('upload');
  const [dragActive, setDragActive] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const inputId = useId();
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraFlipped, setCameraFlipped] = useState(false);
  const [cameraProcessing, setCameraProcessing] = useState(false);
  const [drawHasContent, setDrawHasContent] = useState(false);
  const [previewTransform, setPreviewTransform] =
    useState<PdfSignaturePreviewTransform>(getDefaultSignaturePreviewTransform);
  const [previewDragging, setPreviewDragging] = useState(false);
  const previewViewportRef = useRef<HTMLDivElement | null>(null);
  const previewTransformRef = useRef<PdfSignaturePreviewTransform>(
    getDefaultSignaturePreviewTransform()
  );
  const previewPointersRef = useRef(
    new Map<number, PdfSignaturePreviewPointer>()
  );
  const previewGestureRef = useRef<PdfSignaturePreviewGesture | null>(null);
  const previewNativeGestureRef =
    useRef<PdfSignaturePreviewNativeGesture | null>(null);
  const previewInitialFitRef = useRef<PdfSignaturePreviewInitialFit>('default');
  const pasteTargetRef = useRef<HTMLDivElement | null>(null);
  const drawCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraTargetRef = useRef<HTMLDivElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (!signatureFile) {
      setSignaturePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(signatureFile);
    setSignaturePreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [signatureFile]);

  useEffect(() => {
    previewTransformRef.current = previewTransform;
  }, [previewTransform]);

  const notifySignaturePreparationError = useCallback(
    (error: unknown) => {
      switch (getPdfSignaturePreparationErrorCode(error)) {
        case 'camera-unavailable':
          notify.error({
            title:
              t[
                'com.streamlain.pdf.signature.upload.error.cameraUnavailable'
              ](),
          });
          return;
        case 'clipboard-empty':
          notify.error({
            title:
              t['com.streamlain.pdf.signature.upload.error.clipboardEmpty'](),
          });
          return;
        case 'clipboard-unavailable':
          notify.error({
            title:
              t[
                'com.streamlain.pdf.signature.upload.error.clipboardUnavailable'
              ](),
          });
          return;
        case 'draw-empty':
          notify.error({
            title: t['com.streamlain.pdf.signature.upload.error.drawEmpty'](),
          });
          return;
        case 'image-conversion':
          notify.error({
            title:
              t['com.streamlain.pdf.signature.upload.error.imageConversion'](),
          });
          return;
        case 'unsupported':
        default:
          notify.error({
            title: t['com.streamlain.pdf.signature.upload.error.unsupported'](),
          });
      }
    },
    [t]
  );

  const resetPreviewTransform = useCallback(() => {
    previewPointersRef.current.clear();
    previewGestureRef.current = null;
    previewNativeGestureRef.current = null;
    previewInitialFitRef.current = 'default';
    previewTransformRef.current = getDefaultSignaturePreviewTransform();
    setPreviewTransform(getDefaultSignaturePreviewTransform());
    setPreviewDragging(false);
  }, []);

  const updatePreviewTransform = useCallback(
    (
      updater: (
        current: PdfSignaturePreviewTransform,
        viewport: { width: number; height: number } | null
      ) => PdfSignaturePreviewTransform
    ) => {
      setPreviewTransform(current => {
        const rect = previewViewportRef.current?.getBoundingClientRect();
        const viewport =
          rect && rect.width > 0 && rect.height > 0
            ? {
                width: rect.width,
                height: rect.height,
              }
            : null;
        const next = clampSignaturePreviewTransform(
          updater(current, viewport),
          viewport
        );
        previewTransformRef.current = next;
        return next;
      });
    },
    []
  );

  const getPreviewViewportTransform = useCallback(() => {
    const viewport = previewViewportRef.current;
    if (!viewport) return null;
    const rect = viewport.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
      ...previewTransformRef.current,
      viewportWidth: rect.width,
      viewportHeight: rect.height,
    };
  }, []);

  const setSignatureSourceFile = useCallback(
    (
      sourceFile: PdfSignatureSourceFile | File | null,
      options?: { initialFit?: PdfSignaturePreviewInitialFit }
    ) => {
      if (!sourceFile) {
        setSignatureFile(null);
        resetPreviewTransform();
        previewInitialFitRef.current = 'default';
        return;
      }
      const next =
        sourceFile instanceof File ? { file: sourceFile } : sourceFile;
      if (!getSignatureInputKind(next.file)) {
        notifySignaturePreparationError(
          new PdfSignaturePreparationError('unsupported')
        );
        return;
      }
      resetPreviewTransform();
      previewInitialFitRef.current = options?.initialFit ?? 'default';
      setSignatureFile(next.file);
    },
    [notifySignaturePreparationError, resetPreviewTransform]
  );

  const resolveSignatureSourceFromClipboardPayload = useCallback(
    async (payload: unknown): Promise<PdfSignatureSourceFile | null> => {
      const embedded = extractSignatureFileFromClipboardPayload(payload);
      if (embedded) return embedded;
      if (!resolveClipboardBlob) return null;
      const blobIds = collectSignatureBlobIdsFromClipboardPayload(payload);
      for (const blobId of blobIds) {
        const blob = await resolveClipboardBlob(blobId);
        if (!blob) continue;
        const file =
          blob instanceof File
            ? blob
            : new File([blob], getClipboardBlobFileName(blobId, blob.type), {
                type: blob.type,
              });
        if (!getSignatureInputKind(file)) continue;
        return {
          file,
          suggestedName: getPdfSignatureFileNameWithoutExtension(file.name),
        };
      }
      return null;
    },
    [resolveClipboardBlob]
  );

  const readSignatureSourceFromClipboardData = useCallback(
    async (data: DataTransfer): Promise<PdfSignatureSourceFile | null> => {
      const file = getSignatureFileFromList(data.files);
      if (file) {
        return {
          file,
          suggestedName: getPdfSignatureFileNameWithoutExtension(file.name),
        };
      }
      const htmlPayload = parseBlocksuiteClipboardHtml(
        data.getData('text/html')
      );
      if (htmlPayload) {
        const source =
          await resolveSignatureSourceFromClipboardPayload(htmlPayload);
        if (source) return source;
      }
      const textPayload = tryParseJson(data.getData('text/plain'));
      if (textPayload) {
        return resolveSignatureSourceFromClipboardPayload(textPayload);
      }
      return null;
    },
    [resolveSignatureSourceFromClipboardPayload]
  );

  const readSignatureSourceFromNavigatorClipboard = useCallback(async () => {
    if (!navigator.clipboard?.read) {
      throw new PdfSignaturePreparationError('clipboard-unavailable');
    }
    const items = await navigator.clipboard.read();
    for (const item of items) {
      const imageType = item.types.find(type => type.startsWith('image/'));
      if (imageType) {
        const blob = await item.getType(imageType);
        const file = new File(
          [blob],
          getClipboardBlobFileName('clipboard', imageType),
          {
            type: imageType,
          }
        );
        if (getSignatureInputKind(file)) {
          return {
            file,
            suggestedName:
              t['com.streamlain.pdf.signature.upload.clipboardName'](),
          };
        }
      }
      if (item.types.includes('text/html')) {
        const html = await (await item.getType('text/html')).text();
        const payload = parseBlocksuiteClipboardHtml(html);
        if (payload) {
          const source =
            await resolveSignatureSourceFromClipboardPayload(payload);
          if (source) return source;
        }
      }
      if (item.types.includes('text/plain')) {
        const payload = tryParseJson(
          await (await item.getType('text/plain')).text()
        );
        if (payload) {
          const source =
            await resolveSignatureSourceFromClipboardPayload(payload);
          if (source) return source;
        }
      }
    }
    throw new PdfSignaturePreparationError('clipboard-empty');
  }, [resolveSignatureSourceFromClipboardPayload, t]);

  const pasteSignatureSource = useCallback(
    async (data: DataTransfer) => {
      const source = await readSignatureSourceFromClipboardData(data);
      if (!source) {
        notifySignaturePreparationError(
          new PdfSignaturePreparationError('clipboard-empty')
        );
        return;
      }
      setSignatureSourceFile(source, { initialFit: 'horizontal' });
    },
    [
      notifySignaturePreparationError,
      readSignatureSourceFromClipboardData,
      setSignatureSourceFile,
    ]
  );

  const createSignatureFromSource = useCallback(
    async (source?: PdfSignatureSourceFile) => {
      const sourceFile = source?.file ?? signatureFile;
      if (!sourceFile) return;
      try {
        const previewTransform =
          source?.file && source.file !== signatureFile
            ? null
            : getPreviewViewportTransform();
        const normalizedFile = await normalizeSignatureAppearanceFile(
          sourceFile,
          previewTransform ?? undefined
        );
        const created = await onCreate({
          file: normalizedFile,
          suggestedName:
            source?.suggestedName ||
            getPdfSignatureFileNameWithoutExtension(sourceFile.name),
        });
        if (created) {
          setSignatureFile(null);
          setInputKey(current => current + 1);
          resetPreviewTransform();
        }
      } catch (error) {
        if (error instanceof PdfSignaturePreparationError) {
          notifySignaturePreparationError(error);
          return;
        }
        throw error;
      }
    },
    [
      getPreviewViewportTransform,
      notifySignaturePreparationError,
      onCreate,
      resetPreviewTransform,
      signatureFile,
    ]
  );

  const handleSignatureClipboard = useCallback(async () => {
    notify.warning(
      {
        title: t['com.streamlain.pdf.signature.upload.pasteShortcutTitle'](),
        message: t['com.streamlain.pdf.signature.upload.pasteShortcutMessage']({
          shortcut: pasteShortcut,
        }),
      },
      { duration: 4500 }
    );
    try {
      const source = await readSignatureSourceFromNavigatorClipboard();
      setSignatureSourceFile(source, { initialFit: 'horizontal' });
    } catch (error) {
      notifySignaturePreparationError(error);
    }
  }, [
    notifySignaturePreparationError,
    pasteShortcut,
    readSignatureSourceFromNavigatorClipboard,
    setSignatureSourceFile,
    t,
  ]);

  const handleSignaturePaste = useCallback(
    (event: ReactClipboardEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      void pasteSignatureSource(event.clipboardData);
    },
    [pasteSignatureSource]
  );

  useEffect(() => {
    if (creatorMode !== 'paste') return;

    const frame = requestAnimationFrame(() => {
      pasteTargetRef.current?.focus();
    });

    const onPaste = (event: ClipboardEvent) => {
      if (!event.clipboardData) return;
      event.preventDefault();
      event.stopPropagation();
      void pasteSignatureSource(event.clipboardData);
    };

    document.addEventListener('paste', onPaste, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('paste', onPaste, true);
    };
  }, [creatorMode, pasteSignatureSource]);

  const handleSignatureDrop = useCallback(
    (event: ReactDragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      setDragActive(false);
      const file = getSignatureFileFromList(event.dataTransfer.files);
      if (!file) {
        notifySignaturePreparationError(
          new PdfSignaturePreparationError('unsupported')
        );
        return;
      }
      setSignatureSourceFile(file);
    },
    [notifySignaturePreparationError, setSignatureSourceFile]
  );

  const clearSignatureDrawing = useCallback(() => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    resetSignatureDrawCanvas(canvas);
    setDrawHasContent(false);
  }, []);

  const clearSignatureSource = useCallback(() => {
    setSignatureFile(null);
    setDragActive(false);
    setCameraFlipped(false);
    setInputKey(current => current + 1);
    resetPreviewTransform();
  }, [resetPreviewTransform]);

  const handlePreviewPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      previewPointersRef.current.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });
      previewGestureRef.current = {
        startTransform: previewTransformRef.current,
        startPointers: Array.from(previewPointersRef.current.values()),
      };
      setPreviewDragging(true);
    },
    []
  );

  const handlePreviewPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const pointers = previewPointersRef.current;
      if (!pointers.has(event.pointerId)) return;
      event.preventDefault();
      event.stopPropagation();
      pointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });
      const gesture = previewGestureRef.current;
      if (!gesture) return;
      const currentPointers = Array.from(pointers.values());
      if (!currentPointers.length) return;

      if (gesture.startPointers.length >= 2 && currentPointers.length >= 2) {
        const startCenter = getPreviewPointerCenter(gesture.startPointers);
        const currentCenter = getPreviewPointerCenter(currentPointers);
        const startDistance = getPreviewPointerDistance(gesture.startPointers);
        const currentDistance = getPreviewPointerDistance(currentPointers);
        const rect = event.currentTarget.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return;
        updatePreviewTransform(() => ({
          ...zoomSignaturePreviewTransformBetweenAnchors(
            gesture.startTransform,
            gesture.startTransform.zoom * (currentDistance / startDistance),
            {
              x: startCenter.x - rect.left,
              y: startCenter.y - rect.top,
            },
            {
              x: currentCenter.x - rect.left,
              y: currentCenter.y - rect.top,
            },
            {
              width: rect.width,
              height: rect.height,
            }
          ),
        }));
        return;
      }

      const [startPointer] = gesture.startPointers;
      const [currentPointer] = currentPointers;
      if (!startPointer || !currentPointer) return;
      updatePreviewTransform(() => ({
        ...gesture.startTransform,
        translateX:
          gesture.startTransform.translateX + currentPointer.x - startPointer.x,
        translateY:
          gesture.startTransform.translateY + currentPointer.y - startPointer.y,
      }));
    },
    [updatePreviewTransform]
  );

  const handlePreviewPointerEnd = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      previewPointersRef.current.delete(event.pointerId);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      const remainingPointers = Array.from(previewPointersRef.current.values());
      if (remainingPointers.length) {
        previewGestureRef.current = {
          startTransform: previewTransformRef.current,
          startPointers: remainingPointers,
        };
        return;
      }
      previewGestureRef.current = null;
      setPreviewDragging(false);
    },
    []
  );

  const handlePreviewNativeWheel = useCallback(
    (event: WheelEvent, target: HTMLDivElement) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const rect = target.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const viewport = {
        width: rect.width,
        height: rect.height,
      };
      const isTouchpadPinch = event.ctrlKey || event.metaKey;

      if (isTouchpadPinch) {
        updatePreviewTransform(current =>
          zoomSignaturePreviewTransformAtAnchor(
            current,
            current.zoom * Math.exp(-event.deltaY * 0.006),
            {
              x: event.clientX - rect.left,
              y: event.clientY - rect.top,
            },
            viewport
          )
        );
        return;
      }

      const shiftScrollsHorizontally =
        event.shiftKey && Math.abs(event.deltaX) < Math.abs(event.deltaY);
      const deltaX = shiftScrollsHorizontally ? event.deltaY : event.deltaX;
      const deltaY = shiftScrollsHorizontally ? 0 : event.deltaY;
      updatePreviewTransform(current => ({
        ...current,
        translateX: current.translateX - deltaX,
        translateY: current.translateY - deltaY,
      }));
    },
    [updatePreviewTransform]
  );

  useEffect(() => {
    if (!signaturePreviewUrl) return;
    const viewportElement = previewViewportRef.current;
    if (!viewportElement) return;

    const onWheel = (event: WheelEvent) => {
      handlePreviewNativeWheel(event, viewportElement);
    };

    const getGestureEventState = (event: Event) => {
      const gesture = event as Event & {
        clientX?: number;
        clientY?: number;
        scale?: number;
      };
      const rect = viewportElement.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      return {
        anchor: {
          x: (gesture.clientX ?? rect.left + rect.width / 2) - rect.left,
          y: (gesture.clientY ?? rect.top + rect.height / 2) - rect.top,
        },
        scale: gesture.scale,
        viewport: {
          width: rect.width,
          height: rect.height,
        },
      };
    };

    const onGestureStart = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const state = getGestureEventState(event);
      if (!state) return;
      previewNativeGestureRef.current = {
        startAnchor: state.anchor,
        startTransform: previewTransformRef.current,
      };
    };

    const onGestureChange = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const state = getGestureEventState(event);
      const gesture = previewNativeGestureRef.current;
      if (!state || !gesture || !state.scale) return;
      const acceleratedScale = 1 + (state.scale - 1) * 3;
      const next = zoomSignaturePreviewTransformBetweenAnchors(
        gesture.startTransform,
        gesture.startTransform.zoom * acceleratedScale,
        gesture.startAnchor,
        state.anchor,
        state.viewport
      );
      previewTransformRef.current = next;
      setPreviewTransform(next);
    };

    const onGestureEnd = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      previewNativeGestureRef.current = null;
    };

    viewportElement.addEventListener('wheel', onWheel, {
      capture: true,
      passive: false,
    });
    viewportElement.addEventListener('gesturestart', onGestureStart, {
      capture: true,
    });
    viewportElement.addEventListener('gesturechange', onGestureChange, {
      capture: true,
    });
    viewportElement.addEventListener('gestureend', onGestureEnd, {
      capture: true,
    });

    return () => {
      viewportElement.removeEventListener('wheel', onWheel, {
        capture: true,
      });
      viewportElement.removeEventListener('gesturestart', onGestureStart, {
        capture: true,
      });
      viewportElement.removeEventListener('gesturechange', onGestureChange, {
        capture: true,
      });
      viewportElement.removeEventListener('gestureend', onGestureEnd, {
        capture: true,
      });
    };
  }, [handlePreviewNativeWheel, signaturePreviewUrl]);

  const handlePreviewImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      if (previewInitialFitRef.current !== 'horizontal') return;
      const image = event.currentTarget;

      const applyHorizontalFit = () => {
        const viewport = previewViewportRef.current;
        const rect = viewport?.getBoundingClientRect();
        if (!rect || rect.width <= 0 || rect.height <= 0) return;

        const next = getHorizontalFitSignaturePreviewTransform(image, {
          width: rect.width,
          height: rect.height,
        });
        previewInitialFitRef.current = 'default';
        previewTransformRef.current = next;
        setPreviewTransform(next);
      };

      applyHorizontalFit();
      if (previewInitialFitRef.current === 'horizontal') {
        requestAnimationFrame(applyHorizontalFit);
      }
    },
    []
  );

  useEffect(() => {
    if (creatorMode !== 'draw') return;
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    if (canvas.width !== 720 || canvas.height !== 240) {
      canvas.width = 720;
      canvas.height = 240;
    }
    resetSignatureDrawCanvas(canvas);
    setDrawHasContent(false);
  }, [creatorMode]);

  const startDrawing = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>) => {
      const canvas = event.currentTarget;
      const context = getCanvas2D(canvas);
      const point = getSignatureCanvasPoint(event, canvas);
      event.preventDefault();
      canvas.setPointerCapture(event.pointerId);
      drawingRef.current = true;
      lastPointRef.current = point;
      context.strokeStyle = '#111827';
      context.lineWidth = 5;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.beginPath();
      context.moveTo(point.x, point.y);
      setDrawHasContent(true);
    },
    []
  );

  const continueDrawing = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return;
      const canvas = event.currentTarget;
      const context = getCanvas2D(canvas);
      const point = getSignatureCanvasPoint(event, canvas);
      event.preventDefault();
      const lastPoint = lastPointRef.current ?? point;
      context.beginPath();
      context.moveTo(lastPoint.x, lastPoint.y);
      context.lineTo(point.x, point.y);
      context.stroke();
      lastPointRef.current = point;
      setDrawHasContent(true);
    },
    []
  );

  const stopDrawing = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>) => {
      drawingRef.current = false;
      lastPointRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    []
  );

  const setCreatorModeAndReset = useCallback(
    (mode: PdfSignatureCreatorMode) => {
      setCreatorMode(mode);
      setSignatureFile(null);
      setDragActive(false);
      setCameraFlipped(false);
      setCameraProcessing(false);
      setDrawHasContent(false);
      setInputKey(current => current + 1);
      resetPreviewTransform();
    },
    [resetPreviewTransform]
  );

  const stopCamera = useCallback(() => {
    cameraStreamRef.current?.getTracks().forEach(track => {
      track.stop();
    });
    cameraStreamRef.current = null;
    setCameraStream(null);
  }, []);

  useEffect(() => {
    if (creatorMode === 'camera') return;
    stopCamera();
  }, [creatorMode, stopCamera]);

  useEffect(
    () => () => {
      cameraStreamRef.current?.getTracks().forEach(track => {
        track.stop();
      });
      cameraStreamRef.current = null;
    },
    []
  );

  useEffect(() => {
    const video = cameraVideoRef.current;
    if (!video || !cameraStream) return;
    video.srcObject = cameraStream;
    void video.play().catch(() => {
      notifySignaturePreparationError(
        new PdfSignaturePreparationError('camera-unavailable')
      );
    });
  }, [cameraStream, notifySignaturePreparationError]);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      notifySignaturePreparationError(
        new PdfSignaturePreparationError('camera-unavailable')
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
        },
      });
      stopCamera();
      cameraStreamRef.current = stream;
      setCameraStream(stream);
      setSignatureFile(null);
      setCameraFlipped(false);
      resetPreviewTransform();
    } catch {
      notifySignaturePreparationError(
        new PdfSignaturePreparationError('camera-unavailable')
      );
    }
  }, [notifySignaturePreparationError, resetPreviewTransform, stopCamera]);

  const captureCameraFrame = useCallback(async () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      notifySignaturePreparationError(
        new PdfSignaturePreparationError('camera-unavailable')
      );
      return;
    }
    try {
      setCameraProcessing(true);
      const canvas = captureVideoObjectFitCoverRegionToCanvas(
        video,
        cameraTargetRef.current
      );
      const preparedCanvas = prepareSignatureCanvas(canvas, {
        removeWhiteBackground: true,
      });
      const file = await canvasToPngFile(
        preparedCanvas,
        t['com.streamlain.pdf.signature.upload.cameraName']()
      );
      resetPreviewTransform();
      previewInitialFitRef.current = 'horizontal';
      setSignatureFile(file);
      setCameraFlipped(false);
      stopCamera();
    } catch (error) {
      notifySignaturePreparationError(error);
    } finally {
      setCameraProcessing(false);
    }
  }, [notifySignaturePreparationError, resetPreviewTransform, stopCamera, t]);

  const flipCameraResult = useCallback(async () => {
    if (!signatureFile) return;
    try {
      setCameraProcessing(true);
      const sourceCanvas = await loadImageFileToCanvas(signatureFile);
      const canvas = document.createElement('canvas');
      canvas.width = sourceCanvas.width;
      canvas.height = sourceCanvas.height;
      const context = getCanvas2D(canvas);
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
      context.drawImage(sourceCanvas, 0, 0);
      const file = await canvasToPngFile(canvas, signatureFile.name);
      setSignatureFile(file);
      setCameraFlipped(flipped => !flipped);
    } catch (error) {
      notifySignaturePreparationError(error);
    } finally {
      setCameraProcessing(false);
    }
  }, [notifySignaturePreparationError, signatureFile]);

  const uploadCameraSignature = useCallback(async () => {
    if (!signatureFile) return;
    await createSignatureFromSource({
      file: signatureFile,
      suggestedName: t['com.streamlain.pdf.signature.upload.cameraName'](),
    });
  }, [createSignatureFromSource, signatureFile, t]);

  const uploadDrawnSignature = useCallback(async () => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    try {
      const preparedCanvas = prepareSignatureCanvas(canvas, {
        removeWhiteBackground: false,
      });
      const file = await canvasToPngFile(
        preparedCanvas,
        t['com.streamlain.pdf.signature.upload.drawnName']()
      );
      await createSignatureFromSource({
        file,
        suggestedName: t['com.streamlain.pdf.signature.upload.drawnName'](),
      });
    } catch (error) {
      if (error instanceof PdfSignaturePreparationError) {
        notifySignaturePreparationError(error);
        return;
      }
      throw error;
    }
  }, [createSignatureFromSource, notifySignaturePreparationError, t]);

  const previewImageStyle = useMemo(
    () =>
      ({
        '--pdf-signature-preview-x': `${previewTransform.translateX}px`,
        '--pdf-signature-preview-y': `${previewTransform.translateY}px`,
        '--pdf-signature-preview-zoom': previewTransform.zoom,
      }) as CSSProperties,
    [previewTransform]
  );

  const renderSignaturePreviewEditor = useCallback(() => {
    if (!signaturePreviewUrl) return null;
    return (
      <div
        ref={previewViewportRef}
        className={styles.pdfSignatureCreatorPreviewViewport}
        data-dragging={previewDragging ? 'true' : undefined}
        onPointerDown={handlePreviewPointerDown}
        onPointerMove={handlePreviewPointerMove}
        onPointerUp={handlePreviewPointerEnd}
        onPointerCancel={handlePreviewPointerEnd}
      >
        <img
          alt=""
          className={styles.pdfSignatureCreatorPreviewImage}
          draggable={false}
          onLoad={handlePreviewImageLoad}
          src={signaturePreviewUrl}
          style={previewImageStyle}
        />
      </div>
    );
  }, [
    handlePreviewImageLoad,
    handlePreviewPointerDown,
    handlePreviewPointerEnd,
    handlePreviewPointerMove,
    previewDragging,
    previewImageStyle,
    signaturePreviewUrl,
  ]);

  return (
    <div className={styles.pdfSignatureCreator}>
      <div
        className={styles.pdfSignatureCreatorTabs}
        aria-label={t['com.streamlain.pdf.signature.upload.create']()}
      >
        <button
          type="button"
          aria-label={t['com.streamlain.pdf.signature.upload.modeUpload']()}
          title={t['com.streamlain.pdf.signature.upload.modeUpload']()}
          data-active={creatorMode === 'upload' ? 'true' : undefined}
          onClick={() => setCreatorModeAndReset('upload')}
        >
          <UploadIcon aria-hidden />
        </button>
        <button
          type="button"
          aria-label={t['com.streamlain.pdf.signature.upload.modeDraw']()}
          title={t['com.streamlain.pdf.signature.upload.modeDraw']()}
          data-active={creatorMode === 'draw' ? 'true' : undefined}
          onClick={() => setCreatorModeAndReset('draw')}
        >
          <EditIcon aria-hidden />
        </button>
        <button
          type="button"
          aria-label={t['com.streamlain.pdf.signature.upload.modePaste']()}
          title={t['com.streamlain.pdf.signature.upload.modePaste']()}
          data-active={creatorMode === 'paste' ? 'true' : undefined}
          onClick={() => setCreatorModeAndReset('paste')}
        >
          <PasteIcon aria-hidden />
        </button>
        <button
          type="button"
          aria-label={t['com.streamlain.pdf.signature.upload.modeCamera']()}
          title={t['com.streamlain.pdf.signature.upload.modeCamera']()}
          data-active={creatorMode === 'camera' ? 'true' : undefined}
          onClick={() => {
            setCreatorModeAndReset('camera');
            void startCamera();
          }}
        >
          <CameraIcon aria-hidden />
        </button>
      </div>
      {creatorMode === 'upload' ? (
        <>
          <div className={styles.pdfSignatureCreatorStageFrame}>
            <input
              id={inputId}
              key={inputKey}
              className={styles.pdfSignatureHiddenFileInput}
              type="file"
              accept={PDF_SIGNATURE_INPUT_ACCEPT}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setSignatureSourceFile(event.target.files?.[0] ?? null);
              }}
            />
            {signaturePreviewUrl ? (
              renderSignaturePreviewEditor()
            ) : (
              <label
                htmlFor={inputId}
                className={styles.pdfSignatureDropZone}
                data-active={dragActive ? 'true' : undefined}
                tabIndex={0}
                onPaste={handleSignaturePaste}
                onDragEnter={event => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={event => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={event => {
                  event.preventDefault();
                  setDragActive(false);
                }}
                onDrop={handleSignatureDrop}
              >
                <ImageIcon aria-hidden />
                <span>
                  {t['com.streamlain.pdf.signature.upload.chooseFile']()}
                </span>
                <small>{t['com.streamlain.pdf.signature.upload.help']()}</small>
              </label>
            )}
            {signatureFile ? (
              <button
                type="button"
                className={styles.pdfSignatureCreatorClearButton}
                aria-label={t[
                  'com.streamlain.pdf.signature.upload.clearDrawing'
                ]()}
                title={t['com.streamlain.pdf.signature.upload.clearDrawing']()}
                onClick={clearSignatureSource}
              >
                <DeleteIcon aria-hidden />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            className={styles.pdfSignaturePanelButton}
            data-full-width="true"
            disabled={!signatureFile || isSaving}
            onClick={() => {
              void createSignatureFromSource();
            }}
          >
            <span>{t['com.streamlain.pdf.signature.upload.action']()}</span>
          </button>
        </>
      ) : creatorMode === 'paste' ? (
        <>
          <div className={styles.pdfSignatureCreatorStageFrame}>
            {signaturePreviewUrl ? (
              renderSignaturePreviewEditor()
            ) : (
              <div
                ref={pasteTargetRef}
                className={styles.pdfSignatureDropZone}
                tabIndex={0}
                onPaste={handleSignaturePaste}
              >
                <PasteIcon aria-hidden />
                <span>
                  {t['com.streamlain.pdf.signature.upload.clipboard']()}
                </span>
                <small>
                  {t['com.streamlain.pdf.signature.upload.pasteHelp']({
                    shortcut: pasteShortcut,
                  })}
                </small>
              </div>
            )}
            {signatureFile ? (
              <button
                type="button"
                className={styles.pdfSignatureCreatorClearButton}
                aria-label={t[
                  'com.streamlain.pdf.signature.upload.clearDrawing'
                ]()}
                title={t['com.streamlain.pdf.signature.upload.clearDrawing']()}
                onClick={clearSignatureSource}
              >
                <DeleteIcon aria-hidden />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            className={styles.pdfSignaturePanelButton}
            data-full-width="true"
            disabled={isSaving}
            onClick={() => {
              if (signatureFile) {
                void createSignatureFromSource();
                return;
              }
              void handleSignatureClipboard();
            }}
          >
            <span>{t['com.streamlain.pdf.signature.upload.action']()}</span>
          </button>
        </>
      ) : creatorMode === 'draw' ? (
        <>
          <div className={styles.pdfSignatureDrawStage}>
            <canvas
              ref={drawCanvasRef}
              className={styles.pdfSignatureDrawCanvas}
              aria-label={t['com.streamlain.pdf.signature.upload.drawCanvas']()}
              onPointerDown={startDrawing}
              onPointerMove={continueDrawing}
              onPointerUp={stopDrawing}
              onPointerCancel={stopDrawing}
            />
            {drawHasContent ? (
              <button
                type="button"
                className={styles.pdfSignatureCreatorClearButton}
                aria-label={t[
                  'com.streamlain.pdf.signature.upload.clearDrawing'
                ]()}
                title={t['com.streamlain.pdf.signature.upload.clearDrawing']()}
                onClick={clearSignatureDrawing}
              >
                <DeleteIcon aria-hidden />
              </button>
            ) : null}
          </div>
          <div className={styles.pdfSignaturePanelActions}>
            <button
              type="button"
              className={styles.pdfSignaturePanelButton}
              data-full-width="true"
              disabled={!drawHasContent || isSaving}
              onClick={() => {
                void uploadDrawnSignature();
              }}
            >
              <span>{t['com.streamlain.pdf.signature.upload.action']()}</span>
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.pdfSignatureCameraFrame}>
            {signaturePreviewUrl ? (
              <>
                {renderSignaturePreviewEditor()}
                <button
                  type="button"
                  className={styles.pdfSignatureCameraFlipButton}
                  aria-label={t[
                    'com.streamlain.pdf.signature.upload.cameraFlip'
                  ]()}
                  aria-pressed={cameraFlipped}
                  title={t['com.streamlain.pdf.signature.upload.cameraFlip']()}
                  disabled={cameraProcessing}
                  onClick={() => {
                    void flipCameraResult();
                  }}
                >
                  <FlipDirectionIcon aria-hidden />
                </button>
                <button
                  type="button"
                  className={styles.pdfSignatureCreatorClearButton}
                  aria-label={t[
                    'com.streamlain.pdf.signature.upload.clearDrawing'
                  ]()}
                  title={t[
                    'com.streamlain.pdf.signature.upload.clearDrawing'
                  ]()}
                  disabled={cameraProcessing}
                  onClick={clearSignatureSource}
                >
                  <DeleteIcon aria-hidden />
                </button>
              </>
            ) : (
              <>
                <video
                  ref={cameraVideoRef}
                  className={styles.pdfSignatureCameraVideo}
                  muted
                  playsInline
                />
                <div
                  ref={cameraTargetRef}
                  className={styles.pdfSignatureCameraTargetOverlay}
                  aria-hidden
                />
              </>
            )}
          </div>
          <div className={styles.pdfSignaturePanelActions}>
            <button
              type="button"
              className={styles.pdfSignaturePanelButton}
              data-full-width="true"
              disabled={cameraProcessing || Boolean(signatureFile && isSaving)}
              onClick={() => {
                if (signatureFile) {
                  void uploadCameraSignature();
                  return;
                }
                if (cameraStream) {
                  void captureCameraFrame();
                  return;
                }
                void startCamera();
              }}
            >
              <span>
                {cameraProcessing
                  ? t['com.streamlain.pdf.signature.upload.cameraProcessing']()
                  : signatureFile
                    ? t['com.streamlain.pdf.signature.upload.action']()
                    : cameraStream
                      ? t['com.streamlain.pdf.signature.upload.cameraCapture']()
                      : t['com.streamlain.pdf.signature.upload.cameraStart']()}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
